import { useElementMetadata } from "@/components/contexts/ElementMetadataContext";
import { useDarkModeStore } from "@/store/zustand/darkModeStore";
import type { JsxElement } from "@/utils/jsx/jsxElementTypes";
import { useActiveProject, useCanvasId } from "@/utils/utils";
import { useMemo } from "react";

export const useMetadataForElements = (
  element: JsxElement[],
): {
  imagePath: string | null;
  metadataStatus: "pending" | "done";
  htmlForInstantUpdate: string | null;
  compressedNavTreeForInstantUpdate: string | null;
  width: number | null;
  height: number | null;
}[] => {
  const { metadata } = useElementMetadata();
  const project = useActiveProject();

  const canvasId = useCanvasId();
  const useCanvasDarkMode = useDarkModeStore(
    (state) => state.canvasDarkModeStates[canvasId] || false,
  );

  const metadataForElements = useMemo(() => {
    return element.map(
      (element) => {
        const fileHash = element.fileHash;

        const metadataStatus = "done";

        let imagePath: string | null = null;
        let htmlForInstantUpdate: string | null = null;
        let compressedNavTreeForInstantUpdate: string | null = null;

        const metadataEntry = metadata?.[fileHash || ""];

        // Prioritize previewUrl from elementmetadata table
        if (useCanvasDarkMode && metadataEntry?.darkPreviewUrl) {
          imagePath = metadataEntry.darkPreviewUrl;
        } else if (metadataEntry?.previewUrl) {
          imagePath = metadataEntry.previewUrl;
        }

        if (metadataEntry?.htmlForInstantUpdate) {
          htmlForInstantUpdate = metadataEntry.htmlForInstantUpdate;
        }

        if (metadataEntry?.compressedNavTreeForInstantUpdate) {
          compressedNavTreeForInstantUpdate =
            metadataEntry.compressedNavTreeForInstantUpdate;
        }

        return {
          imagePath,
          metadataStatus,
          htmlForInstantUpdate,
          compressedNavTreeForInstantUpdate,
          width: metadataEntry?.width ?? null,
          height: metadataEntry?.height ?? null,
        };
      },
      [element, metadata, project],
    );
  }, [element, metadata, project, useCanvasDarkMode]);

  return metadataForElements as any;
};
